import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const authContext = useAuth();
  const isDevMode = process.env.NODE_ENV === 'development';

  // Debug log the context value
  console.log('AuthContext value in LoginPage:', authContext);

  // useEffect Hook: Called unconditionally BEFORE the potential early return
  React.useEffect(() => {
    // Only run redirect logic if authContext is available and initialized
    if (authContext) {
        const { authenticated } = authContext; // Destructure inside useEffect
        // Check if user has manually logged out (only relevant for old dev mode logic)
        const hasLoggedOut = localStorage.getItem('mock_logged_out') === 'true';

        // Redirect if authenticated (and not manually logged out in dev mode, though less relevant now)
        if (authenticated && !(isDevMode && hasLoggedOut)) {
          console.log('User authenticated, redirecting to dashboard...');
          navigate('/');
        }
    }
  }, [authContext, navigate, isDevMode]); // Added authContext to dependency array

  // Conditional return for rendering error UI if context is missing
  if (!authContext) {
     console.error("Auth context is missing or not yet initialized in LoginPage!");
     // Render a minimal loading/error state or null while context initializes
     // Returning full UI here can cause issues if context loads later
     return (
        <div className="login-page">
            <div className="login-container">
                <div className="login-card">
                    <h1>Authentifizierung...</h1>
                    <p>Bitte warten.</p>
                </div>
            </div>
        </div>
     );
     // Or simply: return null; if you handle loading state elsewhere
  }

  // Destructure keycloak AFTER confirming context exists for use in handleLogin
  const { keycloak } = authContext;

  const handleLogin = () => {
    // Clear any old dev mode logout flag
    localStorage.removeItem('mock_logged_out');

    // Use the correct login method based on mode (isDevMode check might be redundant now)
    if (isDevMode) {
      // This path might not be reachable if bypassAuth is false, but keep for safety
      console.log('Attempting login in Dev Mode (should use Keycloak)');
      if (keycloak) {
          keycloak.login();
      } else {
          console.error("Cannot login: keycloak object is missing from auth context even in Dev Mode check.");
      }
    } else if (keycloak) {
      // Production or Development with bypassAuth=false
      try {
        console.log('Attempting to login with Keycloak...');
        keycloak.login(); // This should initiate the redirect
      } catch (error) {
        // Fallback logic (might indicate deeper issues if needed)
        console.error('Error calling keycloak.login(), attempting manual redirect:', error);
        const baseUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8081';
        const realm = process.env.REACT_APP_KEYCLOAK_REALM || 'deskbot';
        const clientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'deskbot-frontend';
        const redirectUri = encodeURIComponent(window.location.origin + '/'); // Ensure redirect includes trailing slash if needed by Keycloak config

        const keycloakUrl = `${baseUrl}/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=openid`;

        console.log('Manually redirecting to:', keycloakUrl);
        window.location.href = keycloakUrl;
      }
    } else {
      console.error("Cannot login: keycloak object is missing from auth context.");
    }
  };

  // Render the main login UI
  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-card">
          <h1>Willkommen bei Deskbot</h1>
          <p>Melden Sie sich an, um auf Ihr Dashboard zuzugreifen</p>
          <button className="button login-button" onClick={handleLogin}>
            Einloggen
          </button>
          {/* Removed Dev Mode notice as bypass is off */}
        </div>
      </div>
    </div>
  );
};

export default LoginPage; 